import React, { useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Target, Link as LinkIcon, AlertCircle, ArrowRight } from 'lucide-react';
import { NextStepButton, StepProgressIndicator } from '../components/WorkflowNavigation';

export default function FunctionNetPage() {
    const { allData } = useAppContext();

    const functionNodes = useMemo(() => {
        // Group items to show logical mapping: Product Feature -> Engineering Function -> Component
        // Since FMEA Items map Function -> Failure Mode, we can use FMEA data as our function base.

        return allData.fmeaItems.map(item => ({
            id: item.id,
            function: item.function,
            requirement: item.requirement,
            component: item.itemFunction || 'System Level',
            status: item.status
        }));
    }, [allData.fmeaItems]);

    return (
        <div className="p-6 h-full flex flex-col bg-background/50">
            <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-500/10 p-2 rounded-lg text-blue-500">
                        <Target size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold font-heading text-text-primary">Function Network</h1>
                        <p className="text-text-secondary mt-1">AIAG-VDA Step 3: Function Definition & Matrix</p>
                    </div>
                </div>
                <div className="hidden lg:block">
                    <StepProgressIndicator currentStep={3} completedSteps={[1, 2]} />
                </div>
            </div>

            <div className="flex-1 bg-card rounded-xl border border-border shadow-sm overflow-hidden flex flex-col">
                <div className="overflow-x-auto p-4 flex-1">
                    {functionNodes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary p-8 bg-background/50 rounded-lg border border-dashed border-border/50">
                            <Target size={40} className="mb-4 opacity-30" />
                            <p className="font-medium text-lg">No Functions Mapped</p>
                        </div>
                    ) : (
                        <div className="space-y-4 min-w-[600px]">
                            <div className="flex text-xs font-bold text-text-muted uppercase mb-2 px-2">
                                <span className="w-1/3">Target Property / Element</span>
                                <span className="w-8 flex-shrink-0"></span>
                                <span className="w-1/3">Engineering Function</span>
                                <span className="w-8 flex-shrink-0"></span>
                                <span className="w-1/3">System Requirement</span>
                            </div>

                            {functionNodes.map(node => (
                                <div key={node.id} className="flex items-center gap-2 p-3 bg-background border border-border rounded-lg shadow-sm hover:border-primary/50 transition-colors">
                                    <div className="w-1/3 p-2 bg-slate-50 dark:bg-slate-800 rounded font-medium border border-border/50 truncate">
                                        {node.component}
                                    </div>
                                    <ArrowRight size={16} className="text-text-muted flex-shrink-0" />
                                    <div className="w-1/3 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100 rounded font-medium border border-blue-200 dark:border-blue-800 truncate">
                                        {node.function}
                                    </div>
                                    <ArrowRight size={16} className="text-text-muted flex-shrink-0" />
                                    <div className="w-1/3 p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-100 rounded font-medium border border-emerald-200 dark:border-emerald-800 truncate">
                                        {node.requirement || <span className="text-emerald-900/40 italic">Not defined</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Continue to Next Step */}
            <div className={`p-4 mt-6 rounded-lg border flex items-center justify-between bg-card border-border`}>
                <div>
                    <h3 className="font-semibold text-text-primary">Ready to continue?</h3>
                    <p className="text-sm text-text-secondary">Proceed to analyze failure chains</p>
                </div>
                <NextStepButton currentStep={3} isComplete={functionNodes.length > 0} />
            </div>
        </div>
    );
}
